/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { Form, Input, Checkbox } from 'antd';
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { actions } from "../../store/reducers/authentication";
import { getRedirectPath } from "../../constants/user";
import { useTranslation } from "react-i18next";
import Button from '../../components/Button';
import OnlyDesktop from "../OnlyDesktop";
import AuthLayout from "../../components/Layout/AuthLayout";
import Redirect from "../../components/Redirect";
import { useFirebase } from "../../components/Notifications/useFirebase";
import { TermsAggreed } from "../Register";
import { useApplications } from "../../context/Applications";

const mstp = ({ authentication }) => ({ authentication });
const step = 1;

const AppInvitationComponent = ({
  authentication: {
    authenticated,
    loggingIn,
    errors,
    user,
    registrationData,
  },
  getRegistrationData,
  login,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { applications } = useApplications();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const [form] = Form.useForm();
  const [formErrors, setFormErrors] = useState(null);
  const { firebaseToken } = useFirebase();
  const [termsAgreed, setTermsAgreed] = useState(false);
  const { app } = useParams();

  const appLabel = useMemo(() => applications.find((a) => a.app === app)?.title, [applications, app]);

  const code = useMemo(() => query.get('code'), [query]);

  useEffect(() => {
    if (code && !registrationData) {
      getRegistrationData(code);
    }

    if (registrationData) {
      form.setFieldValue('username', registrationData.email);
    }
  }, [code, getRegistrationData, registrationData]);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    const token = await executeRecaptcha('submit');
    return token;
  }, [executeRecaptcha]);

  useEffect(() => {
    setFormErrors(errors);
  }, [errors]);

  const isSM = () => false;

  const renderErrors = (err, t) => {
    document.querySelectorAll('.ant-form-item')
      .forEach(input => input.classList.add('ant-form-item-has-error'))
    return Object.keys(err).map(key => {
      return err[key].map(error =>
        <div
          key={error}
          className="ant-form-item-explain errors-cont"
          style={{ color: '#ff4d4f' }}>
          {t(error)}
        </div>
      )
    });
  }

  const loginHandle = async () => {
    const token = await handleReCaptchaVerify();

    if (token) {
      setFormErrors(null);
      form.validateFields().then(payload => {
        login({ ...payload, ...registrationData, firebaseToken, termsAgreed, invited: true, app });
      });
    }
  }

  const getResetPassLink = () => {
    if (app === 'mmt') {
      return "/password/reset"
    }
    return `/${app}/password/reset`;
  }

  // const query = queryString.parse(location.search.replace('?', ''));
  const redirectPath = user ? getRedirectPath(user, query && query.get('fallback') ? query.get('fallback') : '') : '';
  return authenticated ? (
    isSM() && redirectPath !== '/app/admin' ? <OnlyDesktop /> : <Redirect to={redirectPath} />
  ) : (
    <AuthLayout
      step={step}
      subTitle={(
        <>
          {t("appInvitation", { app: appLabel })}
          <br />
          <br />
          {t('Use these details to respond to the invitation.')}
        </>
      )}
      app={app}
    >
      <>
        <Form name="login" layout="vertical" form={form}>
          <Form.Item
            required
            name="username"
            label={t("Email Address")}
            rules={[
              {
                required: true,
                message: t("errors::email should not be empty")
              },
              {
                pattern: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                message: t('errors::email is not valid'),
              },
            ]}>
            <Input
              type="email"
              size="large"
              placeholder={t("Enter your email")}
              className="email"
              disabled
              onPressEnter={loginHandle}
            />
          </Form.Item>
          <Form.Item
            required
            name="password"
            label={t("Password")}
            rules={[{
              required: true,
              message: t("errors::password should not be empty")
            }]}>
            <Input.Password
              type="password"
              size="large"
              className="password"
              placeholder={t("Password")}
              onPressEnter={loginHandle}
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link to={getResetPassLink()}>
              {t('Reset password')}
            </Link>
          </div>

          <Form.Item
            name="mailUpdates" valuePropName="checked" noStyle>
            <Checkbox>{t("Sign up to receive updates")}</Checkbox>
          </Form.Item>
          <Form.Item valuePropName="checked" noStyle>
            <TermsAggreed
              t={t}
              termsAgreed={termsAgreed}
              setTermsAgreed={setTermsAgreed}
              app={process.env.REACT_APP_APP}
            />
          </Form.Item>
        </Form>
        {formErrors ? (
          renderErrors(formErrors, t)
        ) : null}
        <div className="buttons">
          <Button disabled={!termsAgreed} onClick={loginHandle} type="primary" block className="normal-br">
            {t('Join Application')}
          </Button>
          <Link to="/">
            <Button block className="normal-br default">
              {t('Decline Invitation')}
            </Button>
          </Link>
        </div>
      </>
    </AuthLayout>
  );
};

const MappedAppInvitation = connect(mstp, actions)(AppInvitationComponent);

export default function AppInvitation() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_PUBLIC_KEY}>
      <MappedAppInvitation />
    </GoogleReCaptchaProvider>
  );
}

